import { useEffect, useMemo, useState } from 'react';
import type { DisplayStateDto, DrawingDto, DrawingStartedDto, WinnerRevealedDto } from '@shared/types';
import { Wheel } from '@shared/Wheel';
import { Confetti } from '@shared/Confetti';
import { initials, localDateTime, money, titleCase } from '@shared/format';
import { HubEvents } from '@shared/realtime';
import { errorMessage, useApp } from '../context';
import { navigate } from '../router';
import { Card, ErrorBox, Loading, Pill, TopBar } from '../components/ui';

export function DrawingLive({ drawingId }: { drawingId: string }) {
  const { api, hub, toast } = useApp();
  const [drawing, setDrawing] = useState<DrawingDto | null>(null);
  const [started, setStarted] = useState<{ s: DrawingStartedDto; at: number } | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState(false);

  const load = async () => {
    try {
      const d = await api.get<DrawingDto>(`/api/drawings/${drawingId}`);
      setDrawing(d);
      if (d.status === 'Started') {
        const st = await api.get<DisplayStateDto>(`/api/displays/${encodeURIComponent(d.displayId)}/state`);
        if (st.started && st.started.drawing.drawingId === drawingId) setStarted((cur) => cur ?? { s: st.started!, at: Date.now() });
      }
    } catch (e) { setError(errorMessage(e)); }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [drawingId]);

  useEffect(() => {
    if (!hub) return;
    const onStarted = (s: DrawingStartedDto) => { if (s.drawing.drawingId === drawingId) setStarted((cur) => cur ?? { s, at: Date.now() }); };
    const onWinner = (w: WinnerRevealedDto) => { if (w.drawingId === drawingId) void load(); };
    hub.on(HubEvents.DrawingStarted, onStarted);
    hub.on(HubEvents.WinnerRevealed, onWinner);
    // Poll as a safety net while the spin is in progress.
    const t = setInterval(() => { if (drawing?.status === 'Started') void load(); }, 3_000);
    return () => { hub.off(HubEvents.DrawingStarted, onStarted); hub.off(HubEvents.WinnerRevealed, onWinner); clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hub, drawingId, drawing?.status]);

  const names = useMemo(() => started ? [...started.s.drawing.entrants].sort((a, b) => a.index - b.index).map((e) => e.displayName) : [], [started]);
  const spin = useMemo(() => started ? {
    winnerIndex: started.s.winnerIndex, landingJitter: started.s.landingJitter, startedAtUtc: started.s.startedAtUtc,
    serverNowUtc: started.s.serverNowUtc, spinSeconds: started.s.drawing.spinSeconds, receivedAtMs: started.at,
  } : null, [started]);

  const returnToBoard = async () => {
    try { await api.post(`/api/drawings/${drawingId}/reset-display`); toast('Display returned to leaderboard'); navigate('/'); }
    catch (e) { toast(errorMessage(e), 'error'); }
  };

  if (error) return <div className="page"><TopBar title="Drawing" back="/" /><ErrorBox message={error} onRetry={() => { setError(null); void load(); }} /></div>;
  if (!drawing) return <div className="page"><TopBar title="Drawing" back="/" /><Loading /></div>;

  if (drawing.status === 'Completed') {
    return (
      <div className="page live-complete">
        <TopBar title="Drawing Complete" back="/" />
        <div className="celebrate">
          <Confetti density={90} />
          <div className="celebrate-badge">{initials(drawing.winnerName ?? '?')}</div>
          <div className="celebrate-congrats">CONGRATULATIONS!</div>
          <div className="celebrate-name">{drawing.winnerName}</div>
          <div className="celebrate-type">{titleCase(drawing.drawingType)} Winner</div>
          <div className="celebrate-prize">{drawing.prizeName}{drawing.prizeValue > 0 ? ` — ${money(drawing.prizeValue)}` : ''}</div>
        </div>
        <div className="stack">
          <button className="btn btn-primary btn-block" onClick={returnToBoard}>Return to Leaderboard</button>
          <button className="btn btn-secondary btn-block" onClick={() => setDetails((v) => !v)}>{details ? 'Hide' : 'View'} Drawing Details</button>
        </div>
        {details && (
          <Card>
            <div className="kv"><span>Reference</span><span>{drawing.reference}</span></div>
            <div className="kv"><span>Period</span><span>{drawing.periodLabel}</span></div>
            <div className="kv"><span>Started by</span><span>{drawing.startedBy}</span></div>
            <div className="kv"><span>Started</span><span>{localDateTime(drawing.startedAtUtc)}</span></div>
            <div className="kv"><span>Completed</span><span>{localDateTime(drawing.completedAtUtc)} ({drawing.completionSource})</span></div>
            <div className="kv"><span>Entrants</span><span>{drawing.entries.length}</span></div>
        <div className="kv"><span>Winner</span><span>{drawing.winnerPickedByAdmin ? 'Picked by admin' : 'Random draw'}</span></div>
            <div className="entrants">
              {[...drawing.entries].sort((a, b) => a.displayOrder - b.displayOrder).map((e) => (
                <div key={e.employeeId} className={`entrant ${e.employeeId === drawing.winnerEmployeeId ? 'win' : ''}`}>
                  <span>{e.displayOrder + 1}. {e.displayName}</span><span className="tabular">{e.tokenTotalAtLock}</span>
                </div>
              ))}
            </div>
            <button className="btn-link" onClick={() => navigate(`/audit?drawingId=${drawing.drawingId}`)}>View audit trail</button>
          </Card>
        )}
      </div>
    );
  }

  if (drawing.status !== 'Started') {
    return <div className="page"><TopBar title="Drawing" back="/" /><Card><p>This drawing is {drawing.status.toLowerCase()}.</p>
      <button className="btn btn-primary btn-block" onClick={() => navigate(`/drawings/${drawing.drawingId}`)}>Open setup</button></Card></div>;
  }

  return (
    <div className="page live">
      <TopBar title="Drawing in Progress" back="/" right={<Pill kind="bad">● LIVE</Pill>} />
      <div className="live-wheel">
        {spin ? <Wheel names={names} spin={spin} size={340} logoSrc="./brand/kes-logo-transparent.png" onRemaining={setRemaining} /> : <Loading label="Syncing with display…" />}
      </div>
      <div className="live-count">
        <div className="live-num tabular">{remaining === null ? '—' : remaining > 0 ? remaining.toFixed(1) : '0.0'}</div>
        <div className="muted">{remaining !== null && remaining <= 0 ? 'Revealing winner…' : 'seconds'}</div>
      </div>
      <Card>
        <div className="kv"><span>Prize</span><span>{drawing.prizeName}{drawing.prizeValue > 0 ? ` (${money(drawing.prizeValue)})` : ''}</span></div>
        <div className="kv"><span>Entrants</span><span>{drawing.entries.length}</span></div>
        <p className="muted small">The winner is already locked on the server. Closing this screen does not affect the drawing.</p>
      </Card>
    </div>
  );
}
